"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLanguageFromLocaleIsoCode = exports.getShortestLocaleCode = exports.getNotifications = exports.getCompanyInformation = exports.getLanguageFromStory = exports.getLanguageByShortRegionCode = exports.getLanguageByNeutralLocaleCode = exports.getLanguageByExactLocaleCode = exports.getUsedLanguages = exports.getDefaultLanguage = exports.getLanguageDisplayName = void 0;
const localeObject_1 = require("./localeObject");
function isOnlyCulture(culture, languages) {
    const numberOfLanguages = languages.filter(({ locale: { language_code } }) => language_code === culture.language_code).length;
    return numberOfLanguages === 1;
}
/**
 * @param mode - defaults to 'native', determines whether to return the native language name or the english language name
 *
 * @returns the display name of the locale in its native language
 *
 * If there's only one culture used in a specific language,
 * we strip the culture name completely.
 *
 * Examples:
 *  - English (Global), Spanish (Spain)
 *  - -> English, Spanish
 *  - English (Global), English (UK), Spanish (Spain)
 *  - -> English (Global), English (UK), Spanish
 */
function getLanguageDisplayName(language, languages, mode = 'native') {
    const { locale } = language;
    const displayedName = mode === 'native' ? locale.native_name : locale.name;
    if (isOnlyCulture(locale, languages)) {
        const cultureNameIndex = displayedName.indexOf('(');
        if (cultureNameIndex !== -1) {
            return displayedName.slice(0, cultureNameIndex - 1);
        }
    }
    return displayedName;
}
exports.getLanguageDisplayName = getLanguageDisplayName;
/**
 * @returns Language set as default in the Newsroom Settings.
 */
function getDefaultLanguage(languages) {
    return languages.find(({ is_default }) => is_default) || languages[0];
}
exports.getDefaultLanguage = getDefaultLanguage;
/**
 * @returns Only languages having at least one published story
 */
function getUsedLanguages(languages) {
    return languages.filter((language) => language.public_stories_count > 0);
}
exports.getUsedLanguages = getUsedLanguages;
function getLanguageByExactLocaleCode(languages, locale) {
    const localeCode = locale.toUnderscoreCode();
    return languages.find(({ code }) => code === localeCode);
}
exports.getLanguageByExactLocaleCode = getLanguageByExactLocaleCode;
// See https://github.com/prezly/prezly/blob/master/lib/model/CulturePeer.php#L123
function getLanguageByNeutralLocaleCode(languages, locale) {
    const neutralLanguageCode = locale.toNeutralLanguageCode();
    // Try to look in used cultures first (giving priority to used ones)
    const usedLanguages = getUsedLanguages(languages);
    const usedLanguage = usedLanguages.find(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toNeutralLanguageCode() === neutralLanguageCode);
    if (usedLanguage) {
        return usedLanguage;
    }
    // Search in all languages
    return languages.find(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toNeutralLanguageCode() === neutralLanguageCode);
}
exports.getLanguageByNeutralLocaleCode = getLanguageByNeutralLocaleCode;
// See https://github.com/prezly/prezly/blob/master/lib/model/CulturePeer.php#L159
function getLanguageByShortRegionCode(languages, locale) {
    const shortRegionCode = locale.toRegionCode();
    // Try to look in used cultures first (giving priority to used ones)
    const usedLanguages = getUsedLanguages(languages);
    const usedLanguage = usedLanguages.find(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toRegionCode() === shortRegionCode);
    if (usedLanguage) {
        return usedLanguage;
    }
    // Search in all languages
    return languages.find(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toRegionCode() === shortRegionCode);
}
exports.getLanguageByShortRegionCode = getLanguageByShortRegionCode;
function getLanguageFromStory(languages, story) {
    const { code: storyLocaleCode } = story.culture;
    return languages.find(({ code }) => code === storyLocaleCode);
}
exports.getLanguageFromStory = getLanguageFromStory;
/**
 * Extracts company information for the selected locale
 */
function getCompanyInformation(languages, locale) {
    const currentLanguage = getLanguageByExactLocaleCode(languages, locale) || getDefaultLanguage(languages);
    return currentLanguage.company_information;
}
exports.getCompanyInformation = getCompanyInformation;
/**
 * Get newsroom notifications for the selected locale
 */
function getNotifications(languages, locale) {
    const currentLanguage = getLanguageByExactLocaleCode(languages, locale) || getDefaultLanguage(languages);
    return currentLanguage.notifications;
}
exports.getNotifications = getNotifications;
/**
 * Get the shortest locale code possible from full locale code
 * First: try shorting to neutral language code (there should be no locales with the same language code)
 * Then: try shorting to region code (there should be no locales with the same region code)
 * Finally: return the original locale code (shorting is not possible)
 *
 * @param locale A LocaleObject constructed from FULL locale code (taken straight from the selected language)
 */
function getShortestLocaleCode(languages, locale) {
    const localeCode = locale.toUnderscoreCode();
    const defaultLanguage = getDefaultLanguage(languages);
    // If it's a default locale, return false (no locale needed in URL)
    if (localeCode === defaultLanguage.code) {
        return false;
    }
    // Undocumented feature: you can completely disable locale shortening by setting `NEXT_PUBLIC_BYPASS_LOCALE_CODE_SHORTENING` env variable.
    // Currently used by a custom theme.
    if (process.env.NEXT_PUBLIC_BYPASS_LOCALE_CODE_SHORTENING) {
        return localeCode;
    }
    // Try shorting to neutral language code
    const neutralLanguageCode = locale.toNeutralLanguageCode();
    const matchingLanguagesByNeutralCode = languages.filter(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toNeutralLanguageCode() === neutralLanguageCode ||
        code === neutralLanguageCode);
    if (matchingLanguagesByNeutralCode.length === 1) {
        return neutralLanguageCode;
    }
    // Try shorting to region code
    const shortRegionCode = locale.toRegionCode();
    const matchingLanguagesByRegionCode = languages.filter(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toRegionCode() === shortRegionCode);
    // Prevent collision with neutral language codes
    const lowerCasedShortRegionCode = shortRegionCode.toLowerCase();
    const matchingNeutralLanguagesByRegionCode = languages.filter(({ code }) => localeObject_1.LocaleObject.fromAnyCode(code).toNeutralLanguageCode() === lowerCasedShortRegionCode ||
        code === lowerCasedShortRegionCode);
    if (matchingLanguagesByRegionCode.length === 1 &&
        // If there are 2 or more matching neutral languages, it means that there are no languages that can be shortened to neutral code
        matchingNeutralLanguagesByRegionCode.length !== 1 &&
        // We don't want just numbers in our region code
        Number.isNaN(Number(shortRegionCode))) {
        return shortRegionCode;
    }
    // Return the original (exact) code if shortening is not possible
    return localeCode;
}
exports.getShortestLocaleCode = getShortestLocaleCode;
/**
 * Get matching language for the requested locale
 * The logic is reversed from `getShortestLocaleCode`, so that it "unwraps" the possible variants with no collisions
 *
 * First: get by exact match
 * Then: get by matching region code part
 * Finally: get by matching language code part
 *
 * @param locale A LocaleObject constructed from the requested locale code (taken straight from the URL)
 */
function getLanguageFromLocaleIsoCode(languages, locale) {
    // Prefer exact match
    const exactMatchedLanguage = getLanguageByExactLocaleCode(languages, locale);
    if (exactMatchedLanguage) {
        return exactMatchedLanguage;
    }
    // If locale code is not region independent, it means that it's not a shortened code (consists of two parts).
    // If it didn't match by exact code, there's no reason to check for region/language code match
    if (!locale.isRegionIndependent) {
        return undefined;
    }
    // We're excluding the default language, since it shouldn't have a URL slug
    const languagesWithoutDefault = languages.filter(({ is_default }) => !is_default);
    const shortRegionCode = locale.toRegionCode();
    const neutralLanguageCode = locale.toNeutralLanguageCode();
    const languageWithSameRegionAndNeutralCode = languagesWithoutDefault.find(({ code }) => {
        const comparingLocale = localeObject_1.LocaleObject.fromAnyCode(code);
        const hasSameNeutralCode = comparingLocale.toNeutralLanguageCode() === neutralLanguageCode;
        const hasSameRegionCode = comparingLocale.toRegionCode() === shortRegionCode;
        return hasSameNeutralCode && hasSameRegionCode;
    });
    if (languageWithSameRegionAndNeutralCode) {
        return languageWithSameRegionAndNeutralCode;
    }
    const regionMatchedLanguage = getLanguageByShortRegionCode(languagesWithoutDefault, locale);
    if (regionMatchedLanguage) {
        return regionMatchedLanguage;
    }
    const neutralMatchedLanguage = getLanguageByNeutralLocaleCode(languagesWithoutDefault, locale);
    if (neutralMatchedLanguage) {
        return neutralMatchedLanguage;
    }
    return undefined;
}
exports.getLanguageFromLocaleIsoCode = getLanguageFromLocaleIsoCode;
//# sourceMappingURL=languages.js.map